import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { createPortal } from 'react-dom';

import { ChevronDown } from 'lucide-react';
const CustomSelect = ({
    id,
    value,
    onChange,
    options = [],
    placeholder,
    disabled = false,
    error = false,
    className = '',
    dropdownClassName = '',
    isOpen,
    onToggle,
    testId,
    renderOption,
    renderValue,
    usePortal = false,
}) => {
    const { t } = useTranslation();
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                buttonRef.current && !buttonRef.current.contains(event.target)) {
                if (isOpen) {
                    onToggle(false);
                }
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen, onToggle]);

    useEffect(() => {
        if (!isOpen || !usePortal || !buttonRef.current) return;

        const updatePosition = () => {
            if (!buttonRef.current) return;
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 8,
                left: rect.left,
                width: rect.width,
            });
        };

        updatePosition();

        // Reposition on any scrolling ancestor or viewport resize so the
        // portaled dropdown stays attached to its trigger button.
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, usePortal]);

    const getDisplayValue = () => {
        if (renderValue) {
            return renderValue(value);
        }
        
        if (value === '' || value === null || value === undefined) {
            return placeholder || t("common.select_dot");
        }
        
        const selectedOption = options.find(opt => 
            typeof opt === 'object' ? opt.value === value : opt === value
        );
        
        if (selectedOption) {
            return typeof selectedOption === 'object' ? selectedOption.label : selectedOption;
        }
        
        return value;
    };

    const handleOptionClick = (optionValue) => {
        onChange(optionValue);
        onToggle(false);
    };

    const renderDropdown = () => {
        if (!isOpen || disabled) return null;

        const dropdownContent = (
            <div
                ref={dropdownRef}
                data-testid={testId ? `${testId}-dropdown` : undefined}
                className={`bg-white rounded-lg shadow-xl p-2 border border-gray-200 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-[#4ABDBD] scrollbar-track-gray-100 ${dropdownClassName}`}
                style={usePortal ? {
                    position: 'fixed',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                    zIndex: 99999
                } : { zIndex: 9999 }}
            >
                {options.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500" data-testid={testId ? `${testId}-empty-text` : undefined}>
                        {placeholder}
                    </div>
                ) : (
                    options.map((option, index) => {
                        const optionValue = typeof option === 'object' ? option.value : option;
                        const optionLabel = typeof option === 'object' ? option.label : option;
                        const isDisabled = typeof option === 'object' && option.disabled;
                        const isSelected = value === optionValue;
                        // Stable per-option testid keyed by value when available, else
                        // the row index — callers can rely on `option-{value}` for
                        // string/number option lists, falling back to index for
                        // object-only enumerations.
                        const optionTestId = testId
                            ? `${testId}-option-${optionValue ?? index}`
                            : undefined;

                        if (renderOption) {
                            return (
                                <div
                                    key={index}
                                    data-testid={optionTestId}
                                    onClick={() => !isDisabled && handleOptionClick(optionValue)}
                                >
                                    {renderOption(option, isSelected, isDisabled)}
                                </div>
                            );
                        }

                        return (
                            <button
                                key={index}
                                type="button"
                                data-testid={optionTestId}
                                onClick={() => !isDisabled && handleOptionClick(optionValue)}
                                disabled={isDisabled}
                                className={`w-full text-left px-4 py-2 text-sm rounded-md mb-1 transition-all duration-300 ${
                                    isSelected
                                        ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                                        : isDisabled
                                        ? 'text-gray-400 cursor-not-allowed opacity-50'
                                        : 'text-gray-700 hover:bg-gradient-to-r hover:from-[#4ABDBD] hover:to-[#3A9B9B] hover:text-white cursor-pointer'
                                }`}
                            >
                                {optionLabel}
                            </button>
                        );
                    })
                )}
            </div>
        );

        if (usePortal) {
            return createPortal(dropdownContent, document.body);
        }

        return dropdownContent;
    };

    return (
        <div className="relative" data-testid={testId ? `${testId}-container` : undefined}>
            <button
                ref={buttonRef}
                id={id}
                type="button"
                onClick={() => !disabled && onToggle(!isOpen)}
                disabled={disabled}
                data-testid={testId}
                className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 flex items-center justify-between ${
                    error ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD]'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
            >
                <span className="truncate" data-testid={testId ? `${testId}-value-text` : undefined}>{getDisplayValue()}</span>
                <ChevronDown
                    data-testid={testId ? `${testId}-chevron-icon` : undefined}
                    className={`ml-2 flex-shrink-0 transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>
            
            {!usePortal && (
                <div className="absolute left-0 top-full mt-2 w-full" style={{ zIndex: 9999 }}>
                    {renderDropdown()}
                </div>
            )}
            
            {usePortal && renderDropdown()}
        </div>
    );
};

export default CustomSelect;

